import EditController from "@/core/EditController";
import { isRequired } from "@/helpers/validators";
import api from '@/core/api';
import { query } from '@/api/db';
import Swal from 'sweetalert2';
import Bus from '@/core/bus';
import { h } from 'vue';
import dayjs from 'dayjs';
import { report } from '@/helpers/reports';

export default class InvoiceRequirementsEditController extends EditController {
    public itemsGrid: any = null;
    private isAutoRequesting = false;

    constructor(protected form: any, protected props: any) {
        super(form, props);

        // Панель кнопок с кнопкой печати
        const panelFun: any[] = [];

        if (!this.readonly.value && (props.store.model.access.update || props.store.model.access.create)) {
            panelFun.push({ save: {} });
        }

        panelFun.push({
            print: {
                caption: 'Печать',
                title: 'Печать накладной-требования',
                icon: h('i', { class: 'icon icon-printer' }),
                class: 'btn btn-outline-secondary',
                onClick: async () => {
                    const data = this.form.value?.data;
                    if (!data?.id) {
                        Swal.fire({ title: 'Сначала сохраните накладную-требование', icon: 'warning', timer: 2000, showConfirmButton: false });
                        return;
                    }
                    const reqNumber = data.requirement_number || '';
                    await report(
                        `Накладная-требование № ${reqNumber}`,
                        'nsi.invoice_requirement_detail_print',
                        { id: data.id }
                    );
                },
            },
        });

        panelFun.push({ close: {} });

        this.createPanelFun(panelFun);
    }

    setItemsGrid(grid: any) {
        this.itemsGrid = grid;
    }

    settings() {
        const self = this;
        return Object.assign(this.props.config, {
            onCreate: async (store: any) => {
                // Статус выставляется автоматически после сохранения (авторезерв)
                // Автогенерация номера накладной-требования при создании
                if (!store.data.requirement_number) {
                    try {
                        const response: any = await api.query('/nakladnye/invoice-requirements/next-number');
                        if (response?.next_number) {
                            store.data.requirement_number = response.next_number;
                        }
                    } catch (error) {
                        // Игнорируем ошибки
                    }
                }
                // Дата по умолчанию — сегодня
                if (!store.data.approval_date) {
                    store.data.approval_date = dayjs().format('YYYY-MM-DD');
                }
                // Автоподстановка «Отпущено» — Центральный склад
                if (!store.data.issued_by_id) {
                    try {
                        const resp: any = await query({
                            table: 'consignees',
                            method: 'read',
                            params: {
                                filters: [{ field: 'is_central', value: true }],
                                limit: 1
                            }
                        });
                        if (resp?.complete && resp.data?.rows?.length > 0) {
                            const central = resp.data.rows[0];
                            store.data.issued_by_id = central.id;
                            store.data._issued_by_id = central.name;
                            // Синхронизируем ФИО МОЛ
                            if (central.mol_fio) {
                                store.data.issued_mol_fio = central.mol_fio;
                            }
                        }
                    } catch (e) {
                        console.error('Ошибка при загрузке центрального склада:', e);
                    }
                }
            },
            onAfterLoad: async (store: any) => {
                // Автозаполнение ФИО МОЛ при загрузке данных
                if (store?.data) {
                    // Заполняем ФИО МОЛ затребовано (всегда обновляем, если есть requested_by_id)
                    if (store.data.requested_by_id) {
                        await this.updateMolFioFromConsignee(store.data.requested_by_id, store);
                    }
                    // Заполняем ФИО МОЛ отпущено (всегда обновляем, если есть issued_by_id)
                    if (store.data.issued_by_id) {
                        await this.updateIssuedMolFioFromConsignee(store.data.issued_by_id, store);
                    }

                    // Пересчитываем суммы позиций по средней стоимости (как в оборотке)
                    if (Array.isArray(store.data.items) && store.data.items.length > 0) {
                        for (const item of store.data.items) {
                            if (item?.deletionmark) continue;
                            const qty = parseFloat(item?.quantity_requested) || 0;
                            if (!item?.medicament_stock_id || qty <= 0) continue;
                            const amount = await this.calculateAmountByStock(item.medicament_stock_id, qty);
                            item.amount = amount.toFixed(2);
                        }
                    }
                }
            },
            fields: {
                id: {
                    config: {
                        visible: false
                    }
                },
                requirement_number: {
                    config: {
                        readonly: true
                    }
                },
				department_id: {
					description: 'Отделение',
					config: {
						visible: false
					}
				},
                requested_by_id: {
                    description: 'Затребовано',
                    validation: {
                        isRequired
                    },
                    config: {
                        reference: 'consignees',
                        displayField: 'name',
                        // При выборе отделения (затребовано) подтягиваем ФИО МОЛ
                        onSelect: async (value: any, store: any) => {
                            await this.updateMolFioFromConsignee(value, store);
                        },
                        onChange: async (value: any, store: any) => {
                            await this.updateMolFioFromConsignee(value, store);
                        }
                    }
                },
                issued_by_id: {
                    description: 'Отпущено',
                    config: {
                        reference: 'consignees',
                        displayField: 'name',
                        // При выборе отделения (отпущено) подтягиваем ФИО МОЛ
                        onSelect: async (value: any, store: any) => {
                            await this.updateIssuedMolFioFromConsignee(value, store);
                        },
                        onChange: async (value: any, store: any) => {
                            await this.updateIssuedMolFioFromConsignee(value, store);
                        }
                    }
                },
                approval_date: {
                    validation: {
                        isRequired
                    },
                    config: {
                        triggers: () => {
                            const result: any = [];
                            result.push({
                                text: h('i', { class: 'icon icon-clock' }),
                                title: 'Установить текущее время',
                                onClick: () => {
                                    this.form.value.data.approval_date = dayjs().format('YYYY-MM-DD');
                                }
                            });
                            return result;
                        }
                    }
                },
                approved_by_id: {
                    description: 'Сотрудник',
                    config: {
                        reference: 'users',
                        displayField: 'name',
                        readonly: true
                    }
                },
                status: {
                    config: {
                        readonly: true
                    }
                },
                mol_fio: {
                    config: {
                        visible: false
                    }
                },
                requested_mol_fio: {
                    description: 'ФИО МОЛ затребовано',
                    config: {
                        readonly: true
                    }
                },
                issued_mol_fio: {
                    description: 'ФИО МОЛ отпущено',
                    config: {
                        readonly: true
                    }
                },
                created_at: {
                    config: {
                        visible: false
                    }
                },
                updated_at: {
                    config: {
                        visible: false
                    }
                },
                deletionmark: {
                    config: {
                        visible: false
                    }
                },
                is_deficit: {
                    config: {
                        visible: false
                    }
                },
                items: {
                    table: 'invoice_requirement_items',
                    autoLoad: true,
                    onCreate: async (store: any) => {
                        const requirementId = this.form.value?.data?.id;
                        // № п/п - с 1 и далее очередной
                        if (!store.data.position_number) {
                            if (requirementId) {
                                store.data.position_number = await this.getNextPositionNumber(requirementId);
                            } else {
                                // Если накладная еще не создана, берем номер из существующих позиций в форме
                                const items = this.form.value?.data?.items || [];
                                const maxPosition = items.length > 0 
                                    ? Math.max(...items.map((item: any) => parseInt(item.position_number) || 0))
                                    : 0;
                                store.data.position_number = maxPosition + 1;
                            }
                        }
                        if (!store.data.quantity_issued) {
                            store.data.quantity_issued = 0;
                        }
                        if (!store.data.amount) {
                            store.data.amount = '0.00';
                        }
                    },
                    onAfterLoad: async (gridController: any) => {
                        const items = gridController?.store?.data?.rows || [];
                        const formatNumber = (num: number) => {
                            const str = num.toFixed(3);
                            return str.replace(/\.?0+$/, '');
                        };
                        
                        for (const item of items) {
                            if (item.medicament_stock_id && !item._stock_balance) {
                                try {
                                    const balanceResponse: any = await api.query('/nsi/medicament-stock/' + item.medicament_stock_id + '/recalculate-balance', {
                                        method: 'POST'
                                    });
                                    
                                    if (balanceResponse?.success) {
                                        const balancePackages = parseFloat(balanceResponse.balancePackages) || 0;
                                        const balanceUnits = parseFloat(balanceResponse.balanceUnits) || 0;
                                        item._stock_balance = `${formatNumber(balancePackages)} упак / ${formatNumber(balanceUnits)} нат.ед`;
                                        item._stock_balance_packages = balancePackages;
                                        item._stock_balance_units = balanceUnits;
                                    }
                                } catch (error) {
                                    // Игнорируем ошибки
                                }
                            }
                        }
                        
                        // Принудительно обновляем грид после загрузки остатков
                        if (gridController?.store) {
                            gridController.store.data.rows = [...gridController.store.data.rows];
                        }
                    },
                    config: {
                        sorting: true
                    },
                    fields: this.getItemsFields()
                }
            }
        });
    }

    /**
     * Автоматически заполняет поле ФИО МОЛ затребовано при выборе отделения из справочника consignees.
     * Также синхронизирует department_id с requested_by_id для обратной совместимости.
     */
    async updateMolFioFromConsignee(value: any, store: any) {
        const consigneeId = typeof value === 'string' ? value : (value?.id || value);
        if (!consigneeId) {
            return;
        }

        try {
            // Берём одну запись подразделения по id
            const response: any = await query({
                table: 'consignees',
                method: 'object',
                data: { id: consigneeId }
            });

            const cons = response?.data || response;
            if (!cons) return;

            const formData = this.form.value?.data || store?.data;
            if (!formData) return;

            // Синхронизируем department_id с requested_by_id для обратной совместимости
            formData.department_id = consigneeId;

            // Заполняем поле ФИО МОЛ затребовано, если оно существует в данных отделения
            if (cons.mol_fio) {
                formData.requested_mol_fio = cons.mol_fio;
            } else {
                formData.requested_mol_fio = '';
            }
        } catch (error) {
            console.error('Ошибка при получении ФИО МОЛ для отделения:', error);
        }
    }

    /**
     * Автоматически заполняет поле ФИО МОЛ отпущено при выборе отделения из справочника consignees.
     */
    async updateIssuedMolFioFromConsignee(value: any, store: any) {
        const consigneeId = typeof value === 'string' ? value : (value?.id || value);
        if (!consigneeId) {
            return;
        }

        try {
            // Берём одну запись подразделения по id
            const response: any = await query({
                table: 'consignees',
                method: 'object',
                data: { id: consigneeId }
            });

            const cons = response?.data || response;
            if (!cons) return;

            const formData = this.form.value?.data || store?.data;
            if (!formData) return;

            // Заполняем поле ФИО МОЛ отпущено, если оно существует в данных отделения
            if (cons.mol_fio) {
                formData.issued_mol_fio = cons.mol_fio;
            } else {
                formData.issued_mol_fio = '';
            }
        } catch (error) {
            console.error('Ошибка при получении ФИО МОЛ для отделения:', error);
        }
    }

    getItemsFields() {
        return {
            id: {
                config: {
                    visible: false
                }
            },
            invoice_requirement_id: {
                config: {
                    visible: false
                }
            },
            position_number: {
                description: '№ п/п',
                config: {
                    readonly: true,
                    width: '70px',
                    sticky: true,
                    sorting: '"invoice_requirement_items"."position_number"'
                }
            },
            medicament_stock_id: {
                description: 'Наименование',
                validation: {
                    isRequired
                },
                config: {
                    reference: 'medicament_stock',
                    displayField: 'product_name',
                    fallbackField: 'product_name',
                    sticky: true,
                    width: '300px',
                    sorting: '"medicament_stock"."product_name"',
                    typeahead: {
                        table: 'medicament_stock',
                        notion: (row: any) => ({
                            title: row.product_name || row.name,
                            notion: `<div>${row.product_name || row.name || ''}</div>` +
                                (row.batch_number ? `<small>Серия: ${row.batch_number}</small>` : '') +
                                (row.manufacturer ? `<small> | ${row.manufacturer}</small>` : '')
                        }),
                        options: {
                            fields: ['id', 'product_name', 'batch_number', 'manufacturer', 'expiry_date', 'unit_id', 'package_count', 'quantity'],
                            limit: 20
                        }
                    },
                    render: (value: any, row: any) => {
                        return row.product_name || row._medicament_stock_id || value || '—';
                    },
                    onChange: async (value: any, form: any) => {
                        if (value) {
                            const stockId = typeof value === 'object' ? value.id : value;
                            await this.fillItemFromStock(stockId, form);
                        }
                    },
                    onSelect: async (value: any, form: any) => {
                        if (value) {
                            const stockId = typeof value === 'object' ? value.id : value;
                            await this.fillItemFromStock(stockId, form);
                        }
                    }
                }
            },
            product_name: {
                description: 'Наименование',
                config: {
                    visible: false
                }
            },
            batch_number: {
                description: 'Серия',
                config: {
                    width: '120px',
                    sorting: '"invoice_requirement_items"."batch_number"'
                }
            },
            expiry_date: {
                description: 'Срок годности',
                config: {
                    width: '130px',
                    sorting: '"invoice_requirement_items"."expiry_date"'
                }
            },
            manufacturer: {
                description: 'Производитель',
                config: {
                    width: '200px',
                    sorting: '"invoice_requirement_items"."manufacturer"'
                }
            },
            country: {
                description: 'Страна',
                config: {
                    width: '150px',
                    sorting: '"invoice_requirement_items"."country"'
                }
            },
            unit_id: {
                description: 'Ед. изм.',
                config: {
                    width: '100px',
                    sorting: '"units"."name"'
                }
            },
            _stock_balance: {
                description: 'Остаток на складе',
                config: {
                    width: '180px',
                    readonly: true,
                    render: (value: any, row: any) => {
                        return row._stock_balance || '—';
                    }
                }
            },
            quantity_requested: {
                description: 'К-во затребовано (нат.ед)',
                validation: {
                    isRequired
                },
                config: {
                    width: '140px',
                    type: 'number',
                    step: 0.001,
                    min: 0,
                    sorting: '"invoice_requirement_items"."quantity_requested"',
                    onChange: async (value: any, form: any) => {
                        const requested = parseFloat(value) || 0;
                        const available = parseFloat(form.data._stock_balance_units) || 0;
                        if (requested > available && available > 0) {
                            Swal.fire({ icon: 'info', title: 'Внимание', text: `Затребовано ${requested.toFixed(3)} нат.ед., доступно ${available.toFixed(3)}. Превышение будет оформлено отдельной заявкой.`, timer: 3000, showConfirmButton: false });
                        }
                        await this.calculateAmount(form);
                    }
                }
            },
            quantity_issued: {
                description: 'К-во отпущено (нат.ед)',
                config: {
                    width: '140px',
                    type: 'number',
                    step: 0.001,
                    min: 0,
                    readonly: true,
                    sorting: '"invoice_requirement_items"."quantity_issued"',
                    display: (value: any) => {
                        if (value === null || value === undefined || value === '') return '';
                        const num = parseFloat(value);
                        if (isNaN(num) || num === 0) return '';
                        return num.toFixed(3).replace(/\.?0+$/, '');
                    }
                }
            },
            amount: {
                description: 'Сумма',
                config: {
                    width: '120px',
                    readonly: true,
                    sorting: '"invoice_requirement_items"."amount"',
                    display: (value: any) => {
                        if (value === null || value === undefined || value === '') return '';
                        const num = parseFloat(value);
                        if (isNaN(num) || num === 0) return '';
                        return num.toFixed(2).replace(/\.?0+$/, '');
                    }
                }
            },
            created_at: {
                config: {
                    visible: false
                }
            },
            updated_at: {
                config: {
                    visible: false
                }
            },
            deletionmark: {
                config: {
                    visible: false
                }
            }
        };
    }

    async fillItemFromStock(value: any, form: any) {
        if (!value) return;

        const stockId = typeof value === 'string' ? value : (value?.id || value);
        if (!stockId) return;

        try {
            const excludeItemId = form.data?.id || null;
            const [stockResponse, availableResponse] = await Promise.all([
                // Получаем одну конкретную карточку М-17 по id
                query({
                    table: 'medicament_stock',
                    method: 'object',
                    data: { id: stockId }
                }),
                api.query('/nsi/medicament-stock/' + stockId + '/available-quantity', {
                    method: 'POST',
                    data: excludeItemId ? { excludeItemId } : {}
                })
            ]) as [any, any];

            const stockData = stockResponse?.data || stockResponse;
            const availableData = (availableResponse as any)?.success ? (availableResponse as any) : null;

            if (stockData) {
                form.data.product_name = stockData.product_name || '';
                form.data.batch_number = stockData.batch_number || '';
                form.data.expiry_date = stockData.expiry_date || null;
                form.data.manufacturer = stockData.manufacturer || '';
                
                const unitId = stockData.unit_id || stockData._unit_id;
                if (unitId) {
                    if (typeof unitId === 'string') {
                        form.data.unit_id = unitId;
                    } else if (typeof unitId === 'number') {
                        const unitNames: any = {
                            1: 'шт', 2: 'упак', 3: 'мл', 4: 'л', 5: 'г', 6: 'кг', 7: 'мг',
                            8: 'таб', 9: 'кап', 10: 'амп', 11: 'фл', 12: 'тюб', 13: 'банк', 14: 'пач', 15: 'бл'
                        };
                        form.data.unit_id = unitNames[unitId] || '';
                    } else {
                        form.data.unit_id = stockData._unit_id || '';
                    }
                } else {
                    form.data.unit_id = '';
                }
                
                form.data.medicament_stock_id = stockId;
                
                const packageCount = parseFloat(stockData.package_count) || 1;
                
                const formatNumber = (num: number) => {
                    const str = num.toFixed(3);
                    return str.replace(/\.?0+$/, '');
                };
                
                if (availableData) {
                    const totalPkg = parseFloat(availableData.totalPackages) || 0;
                    const reservedPkg = parseFloat(availableData.reservedPackages) || 0;
                    const availablePkg = parseFloat(availableData.availablePackages) || 0;
                    const availableUnits = Math.round(availablePkg * packageCount * 1000) / 1000;
                    const totalUnits = Math.round(totalPkg * packageCount * 1000) / 1000;
                    const reservedUnits = Math.round(reservedPkg * packageCount * 1000) / 1000;
                    
                    let stockBalance: string;
                    if (reservedPkg > 0) {
                        stockBalance = `Всего: ${formatNumber(totalPkg)} упак (${formatNumber(totalUnits)} нат.ед) | Резерв: ${formatNumber(reservedPkg)} упак (${formatNumber(reservedUnits)} нат.ед) | Доступно: ${formatNumber(availablePkg)} упак (${formatNumber(availableUnits)} нат.ед)`;
                    } else {
                        stockBalance = `Доступно: ${formatNumber(availablePkg)} упак (${formatNumber(availableUnits)} нат.ед)`;
                    }
                    
                    form.data._stock_balance = stockBalance;
                    form.data._stock_balance_packages = availablePkg;
                    form.data._stock_balance_units = availableUnits;
                } else {
                    form.data._stock_balance = '';
                    form.data._stock_balance_packages = 0;
                    form.data._stock_balance_units = 0;
                }
                form.data._package_count = packageCount;
                
                if (form.data.quantity_requested) {
                    await this.calculateAmount(form);
                }
            }
        } catch (error) {
            console.error('Error loading stock data:', error);
        }
    }

    async calculateAmount(form: any) {
        const quantityRequestedUnits = parseFloat(form.data.quantity_requested) || 0;
        const stockId = form.data.medicament_stock_id;
        
        if (quantityRequestedUnits > 0 && stockId) {
            try {
                const amount = await this.calculateAmountByStock(stockId, quantityRequestedUnits);
                form.data.amount = amount.toFixed(2);
            } catch (error) {
                console.error('Error calculating amount:', error);
                form.data.amount = '0.00';
            }
        } else {
            form.data.amount = '0.00';
        }
    }

    private async calculateAmountByStock(stockId: string, quantityRequestedUnits: number): Promise<number> {
        const response: any = await api.query(`/nsi/medicament-stock/${stockId}/calculate-expense-amount`, {
            method: 'POST',
            data: {
                quantity_units: quantityRequestedUnits
            }
        });
        if (response?.success) {
            return parseFloat(response.amount) || 0;
        }
        return 0;
    }

    async getNextPositionNumber(requirementId: string): Promise<number> {
        if (!requirementId) return 1;

        try {
            const items = this.form.value?.data?.items || [];
            if (items && Array.isArray(items) && items.length > 0) {
                const maxPosition = Math.max(...items.map((item: any) => parseInt(item.position_number) || 0));
                return maxPosition + 1;
            }
            return 1;
        } catch (error) {
            console.error('Error getting next position number:', error);
            return 1;
        }
    }

    async approve() {
        const requirementId = this.form.value?.data?.id;
        if (!requirementId) {
            Swal.fire({ title: 'Ошибка', text: 'Не найдена', icon: 'error', timer: 3000, showConfirmButton: false });
            return;
        }

        try {
            // Проверяем наличие позиций из грида или локального состояния формы
            let validItems: any[] = [];
            
            // Сначала проверяем грид, если он доступен
            if (this.itemsGrid?.value?.controller?.store?.data?.rows) {
                validItems = this.itemsGrid.value.controller.store.data.rows.filter((item: any) => !item.deletionmark);
            }
            
            // Если в гриде нет позиций, проверяем локальное состояние формы
            if (validItems.length === 0) {
                const formItems = this.form.value?.data?.items || [];
                validItems = Array.isArray(formItems) ? formItems.filter((item: any) => !item.deletionmark) : [];
            }
            
            // Если в локальном состоянии нет позиций, проверяем в базе данных
            if (validItems.length === 0) {
                const itemsResponse: any = await query({
                    table: 'invoice_requirement_items',
                    method: 'list',
                    data: {
                        filters: [
                            { field: 'invoice_requirement_id', value: requirementId },
                            { field: 'deletionmark', value: false }
                        ]
                    }
                });

                if (!itemsResponse?.rows || itemsResponse.rows.length === 0) {
                    Swal.fire({ title: 'Ошибка', text: 'Нет позиций', icon: 'error', timer: 3000, showConfirmButton: false });
                    return;
                }
            }

            // Сначала сохраняем текущие данные перед согласованием (включая все позиции)
            const preSaved = await this.form.value?.save();
            if (!preSaved) {
                Swal.fire({ title: 'Ошибка', text: 'Не удалось сохранить', icon: 'error', timer: 3000, showConfirmButton: false });
                return;
            }

            let currentUserId: string | null = null;
            
            if (this.props?.store?.state?.user?.id) {
                currentUserId = this.props.store.state.user.id;
            } else {
                const statusResponse: any = await api.query('/auth/status');
                currentUserId = statusResponse?.id || null;
            }

            if (!currentUserId) {
                Swal.fire({ title: 'Ошибка', text: 'Не определён пользователь', icon: 'error', timer: 3000, showConfirmButton: false });
                return;
            }

            const approvalDate = new Date().toISOString().split('T')[0];

            // Номер требования присваивается после согласования зав. Центральным складом
            if (!this.form.value.data.requirement_number) {
                try {
                    const response: any = await api.query('/nakladnye/invoice-requirements/next-number');
                    if (response?.next_number) {
                        this.form.value.data.requirement_number = response.next_number;
                    }
                } catch (error) {
                    console.error('Error getting next requirement number:', error);
                }
            }

            // Сначала вызываем метод на бэкенде для создания резерва + автосоздания заявки при дефиците
            let approveResponse: any;
            try {
                approveResponse = await api.query('/nakladnye/approve-requirement', {
                    method: 'POST',
                    data: { requirement_id: requirementId }
                });
                
                if (!approveResponse?.success) {
                    throw new Error(approveResponse?.message || 'Ошибка при согласовании');
                }
            } catch (error: any) {
                Swal.fire({ title: 'Ошибка', text: error.message || 'Не удалось согласовать', icon: 'error', timer: 3000, showConfirmButton: false });
                return;
            }

            // Обновляем статус на фронтенде
            this.form.value.data.status = 'requested';
            this.form.value.data.approval_date = approvalDate;
            this.form.value.data.approved_by_id = currentUserId;
            
            const saved = await this.form.value?.save();
            
            if (saved) {
                const store = this.store?.value;
                if (store?.data?.id && typeof store.fetchData === 'function') {
                    await store.fetchData(store.data.id);
                }
                
                // Обновляем грид списка
                Bus.$emit('save-data', {
                    table: 'invoice_requirements',
                    data: this.form.value?.data,
                    newrecord: false
                });
                Bus.$emit('refresh-data', { table: 'invoice_requirements' });
                
                if (approveResponse?.hasDeficit && approveResponse?.deficitItems?.length > 0) {
                    const fmt = (n: number) => n.toFixed(3).replace(/\.?0+$/, '');
                    const deficitHtml = approveResponse.deficitItems.map((d: any) => 
                        `<tr><td>${d.product_name}</td><td class="text-right">${fmt(d.requested)}</td><td class="text-right">${fmt(d.available)}</td><td class="text-right text-danger font-weight-bold">${fmt(d.deficit)}</td></tr>`
                    ).join('');

                    await Swal.fire({
                        title: 'Затребовано',
                        icon: 'warning',
                        html: `<p>По некоторым позициям на складе не хватает. Резерв создан на доступное количество.</p>
                            <div style="max-height: 300px; overflow-y: auto;">
                            <table class="table table-sm table-bordered" style="font-size: 0.85rem;">
                                <thead><tr><th>Наименование</th><th>Затребовано</th><th>На складе</th><th>Нехватка</th></tr></thead>
                                <tbody>${deficitHtml}</tbody>
                            </table>
                            </div>`,
                        width: 700,
                        confirmButtonText: 'Понятно'
                    });
                } else {
                    Swal.fire({ title: 'Затребовано', icon: 'success', timer: 1500, showConfirmButton: false });
                }
            } else {
                Swal.fire({ title: 'Ошибка', text: 'Не удалось сохранить', icon: 'error', timer: 3000, showConfirmButton: false });
            }
        } catch (error: any) {
            Swal.fire({ title: 'Ошибка', text: error.message || 'Ошибка согласования', icon: 'error', timer: 3000, showConfirmButton: false });
        }
    }

    async issue() {
        const requirementId = this.form.value?.data?.id;
        if (!requirementId) {
            Swal.fire({ title: 'Ошибка', text: 'Не найдена', icon: 'error', timer: 3000, showConfirmButton: false });
            return;
        }

        try {
            // Проверяем наличие позиций из грида или локального состояния формы
            let validItems: any[] = [];
            
            if (this.itemsGrid?.value?.controller?.store?.data?.rows) {
                validItems = this.itemsGrid.value.controller.store.data.rows.filter((item: any) => !item.deletionmark);
            }
            
            if (validItems.length === 0) {
                const formItems = this.form.value?.data?.items || [];
                validItems = Array.isArray(formItems) ? formItems.filter((item: any) => !item.deletionmark) : [];
            }
            
            if (validItems.length === 0) {
                const itemsResponse: any = await query({
                    table: 'invoice_requirement_items',
                    method: 'list',
                    data: {
                        filters: [
                            { field: 'invoice_requirement_id', value: requirementId },
                            { field: 'deletionmark', value: false }
                        ]
                    }
                });

                if (!itemsResponse?.rows || itemsResponse.rows.length === 0) {
                    Swal.fire({ title: 'Ошибка', text: 'Нет позиций', icon: 'error', timer: 3000, showConfirmButton: false });
                    return;
                }
            }

            // Сохраняем текущие данные перед отпуском
            const preSaved = await this.form.value?.save();
            if (!preSaved) {
                Swal.fire({ title: 'Ошибка', text: 'Не удалось сохранить', icon: 'error', timer: 3000, showConfirmButton: false });
                return;
            }

            const response: any = await api.query('/nakladnye/issue-requirement', {
                method: 'POST',
                data: { requirement_id: requirementId }
            });

            const store = this.store?.value;
            if (store?.data?.id && typeof store.fetchData === 'function') {
                await store.fetchData(store.data.id);
            }
            
            // Обновляем грид позиций
            if (this.itemsGrid?.value?.controller) {
                await this.itemsGrid.value.controller.fetchData();
            }
            
            Bus.$emit('save-data', {
                table: 'invoice_requirements',
                data: this.form.value?.data,
                newrecord: false
            });
            
            // Проверяем наличие дефицита
            if (response?.partial && response?.deficitItems?.length > 0) {
                const fmt = (n: number) => n.toFixed(3).replace(/\.?0+$/, '');
                const deficitHtml = response.deficitItems.map((d: any) => 
                    `<tr><td>${d.product_name}</td><td class="text-right">${fmt(d.requested)}</td><td class="text-right">${fmt(d.issued)}</td><td class="text-right text-danger font-weight-bold">${fmt(d.deficit)}</td></tr>`
                ).join('');

                await Swal.fire({
                    title: 'Товары отпущены частично',
                    icon: 'warning',
                    html: `<p>По некоторым позициям не хватило остатков на складе. Дефицит остался в резерве.</p>
                        <div style="max-height: 300px; overflow-y: auto;">
                        <table class="table table-sm table-bordered" style="font-size: 0.85rem;">
                            <thead><tr><th>Наименование</th><th>Затребовано</th><th>Отпущено</th><th>Дефицит</th></tr></thead>
                            <tbody>${deficitHtml}</tbody>
                        </table>
                        </div>
                        <p class="mt-2 text-muted"><small>Необходимо закупить недостающие позиции.</small></p>`,
                    width: 700,
                    confirmButtonText: 'Понятно'
                });
            } else {
                Swal.fire({ title: 'Отпущено', icon: 'success', timer: 1500, showConfirmButton: false });
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Не удалось отпустить товары';
            Swal.fire({ title: 'Ошибка', text: errorMessage, icon: 'error', timer: 3000, showConfirmButton: false });
        }
    }


    async afterSave(): Promise<boolean> {
        if (this.isAutoRequesting) {
            return true;
        }

        const data = this.form.value?.data;
        if (!data?.id) {
            return true;
        }

        const status = (data.status || '').toString().trim().toLowerCase();
        const isDeficit = data.is_deficit === true || data.is_deficit === 'true';
        const hasItems = Array.isArray(data.items) && data.items.some((item: any) => !item.deletionmark);

        // Для обычных накладных-требований резерв создаём автоматически после сохранения.
        if (isDeficit || !hasItems || (status && status !== 'draft' && status !== 'new')) {
            Bus.$emit('form-saved', { table: 'invoice_requirements' });
            Bus.$emit('refresh-data', { table: 'invoice_requirements', data });
            return true;
        }

        try {
            this.isAutoRequesting = true;

            const response: any = await api.query('/nakladnye/approve-requirement', {
                method: 'POST',
                data: { requirement_id: data.id }
            });

            if (response?.success && this.store?.value?.fetchData) {
                await this.store.value.fetchData(data.id);
            }

            if (response?.hasDeficit && response?.deficitItems?.length > 0) {
                Swal.fire({
                    title: response?.allDeficit ? 'Затребовано по заявке' : 'Затребовано',
                    text: response?.allDeficit
                        ? 'По всем позициям дефицит — накладная отправлена в "Затребовано по заявке".'
                        : 'По части позиций на складе дефицит. Создан резерв на доступное количество.',
                    icon: 'warning',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error: any) {
            Swal.fire({
                title: 'Ошибка',
                text: error?.message || 'Не удалось автоматически создать резерв по накладной-требованию',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            this.isAutoRequesting = false;
            const currentData = this.form.value?.data || data;
            Bus.$emit('form-saved', { table: 'invoice_requirements' });
            Bus.$emit('refresh-data', { table: 'invoice_requirements', data: currentData });
        }

        return true;
    }

    async beforeSave(): Promise<boolean> {
        const form = this.form.value;
        if (form?.data?.items) {
            const items = Array.isArray(form.data.items) ? form.data.items : [];
            for (let index = 0; index < items.length; index++) {
                const item = items[index];
                if (!item.position_number) {
                    item.position_number = index + 1;
                }
                if (!item?.deletionmark) {
                    const qty = parseFloat(item?.quantity_requested) || 0;
                    if (item?.medicament_stock_id && qty > 0) {
                        const amount = await this.calculateAmountByStock(item.medicament_stock_id, qty);
                        item.amount = amount.toFixed(2);
                    }
                }
            }
        }
        return true;
    }
}

